<template lang="pug">
  .user.row.middle
    .m-right-1.m-left-1.row.middle.center(v-if='!isTeacher')
      checkbox(:updateCheck='toggleSelect' :checked='isSelected' :class='{hidden: hideUserCheck}')
    .row.middle(:class='{"m-left-2": isTeacher, "item-4": edition, "item-5": !edition}')
      img.avatar.m-right-1(:src='user.avatar || profileDefaultImg')
      .column
        .row.name.ellipsis(:class='{edition}')
          h2.text-normal.m4-right-1 {{ user.fullName }}
          h2.text-normal.ellipsis(
            v-if='user.position.positionId'
            :title='getPositionWithSchoolName(user.position)'
          ) {{ getPositionWithSchoolName(user.position) }}
        h2.text-small.dark-gray.email.ellipsis(:title='user.email' :class='{edition}') {{ user.email }}
    h1.text-small.item-2(v-if='!edition || (edition && !isAdmin)') {{ $t(`userRoles.${user.role.toLowerCase()}`) }}
    .item-2(v-else)
      k-select.select(
        withBorder
        :options='roles'
        name='newRole'
        keyName='value'
        labelName='label'
        small
        @selectOption='editRole'
        :preselectedOption='translateRole(user.role)'
      )
    h1.item-2(v-if='!edition && isKreaAdmin') {{ user.secondaryRole ? $t(`userRoles.secondary.${toLowerCamelCase(user.secondaryRole)}`) : '-' }}
    .item-2(v-if='edition && isKreaAdmin')
      k-select.select(
        withBorder
        :options='kreaRoles'
        name='newKreaRole'
        keyName='value'
        labelName='label'
        small
        :emptyOption='$t("team.list.emptyKreaRole")'
        @selectOption='editKreaRole'
        :preselectedOption='translateKreaRole(user.secondaryRole)'
      )
    .m-right-2.row.middle(v-if='!edition' :class='edition ? "item-3" : "item-2"')
      .row(v-if='rowIsTeacher')
        span.row.middle.center.level.m4-right-1(
          v-for='level in sortedLevels'
          :title='$t(`educationalLevels.${getLevel(level)}`)'
        ) {{ getShortLevel(level) }}
      h2.text-small.row.middle.center.m4-left-1(v-else) -
    .item-3.m-right-2.row.middle(v-if='edition')
      custom-tag-input.custom-tag-input(
        v-if='editionRoleIsTeacher'
        withBorder
        small
        :placeholder='$t("team.list.levelPlaceholder")'
        :options='availableLevels'
        name='newLevel'
        keyName='id'
        labelName='label'
        @change='editLevels'
        :value='editionLevels'
        :error='showErrors && levelsError'
      )
      h2.text-small.row.middle.center.m4-left-1(v-else) -
    .item-2
      .column.start(v-if='(isAdmin || isManager) || isTeacher && isConfirmed')
        span.team-status.row.middle.bold(:class='getStatusStyles(user.invitationStatus)')
          simple-svg.m4-right-2(
            :filepath='getStatusIcon(user.invitationStatus)'
            width='16px'
            height='16px'
            :class='`${getStatusStyles(user.invitationStatus)}-icon`'
          )
          | {{ $t(`teamStatus.${user.invitationStatus.toLowerCase()}`) }}
        a.text-small.blue.m4-top-1(v-if='showResendInvitation' @click='resend') {{ $t('team.list.userActions.sendInvitation') }}
    .m-right-4.item-2(v-if='!edition && !isTeacher')
      .row.middle(v-if='isAdmin || (isManager && rowIsTeacher)')
        button.m-right-2.action.row.middle(@click='toggleEdition')
          simple-svg.m4-right-1.dark-gray-icon(:filepath='editIcon' width='15px' height='15px')
          h2.text-normal.dark-gray.bold {{ $t('team.list.userActions.edit') }}
        button.action.row.middle(@click='deleteUser')
          simple-svg.m4-right-1.dark-gray-icon(:filepath='deleteIcon' width='15px' height='15px')
          h2.text-normal.dark-gray.bold {{ $t('team.list.userActions.delete') }}
    .m-right-4.item-2.row.middle(v-if='edition && !isTeacher')
      .row.middle(v-if='isAdmin || (isManager && rowIsTeacher)')
        button.m-right-1.action.border.blue.row.middle(@click='editUser')
          h2.blue.text-normal.dark-gray.bold {{ $t('team.list.userActions.save') }}
        button.action.border.row.middle(@click='toggleEdition')
          h2.text-normal.dark-gray.bold {{ $t('team.list.userActions.cancel') }}
</template>

<script>
import profileDefaultImg from '@/assets/profile_default_img.png'
import checkboxDefaultIcon from '@/assets/ic_checkbox_default_transparent.svg'
import checkboxActiveIcon from '@/assets/ic_checkbox_active.svg'
import editIcon from '@/assets/ic_edit.svg'
import deleteIcon from '@/assets/ic_trash.svg'
import userRole from '../../mixins/userRole'
import CustomTagInput from './CustomTagInput'
import KSelect from '@/components/KSelect'
import { rolesIds, rolesOptions, secondaryRolesIds, secondaryRolesOptions, teamMembersStatus, userPrimaryRoles, userSecondaryRoles } from '../../utils/constants'
import { i18n } from '../../main'
import { mapActions, mapState } from 'vuex'
import { toLowerCamelCase } from '../../utils/string'
import kreaRoles from '../../mixins/kreaRoles'
import userPosition from '@/mixins/userPosition'
import Checkbox from '../Inputs/Checkbox'

export default {
  mixins: [userRole, kreaRoles, userPosition],
  components: { Checkbox, CustomTagInput, KSelect },
  props: {
    user: { type: Object },
    pageNumber: { type: Number }
  },
  data() {
    return {
      editIcon,
      toLowerCamelCase,
      deleteIcon,
      checkboxActiveIcon,
      checkboxDefaultIcon,
      profileDefaultImg,
      edition: false,
      editionLevels: this.user.educationalLevels,
      editionRole: this.user.role,
      editionKreaRole: this.user.secondaryRole,
      showErrors: false,
      levelsError: false
    }
  },
  computed: {
    ...mapState({
      educationalLevels: state => state.profile.educationalLevels,
      showChoiceModal: state => state.behavior.choiceModal.show,
      selectedUsers: state => state.team.selectedUsers,
      users: state => state.team.allUsers.users,
      currentSchool: state => state.profile.userProfile.schoolGroupVO.currentSchool
    }),
    sortedLevels() {
      return this.user.educationalLevels
        .map(level => level)
        .sort((a, b) => a - b)
    },
    hideUserCheck() {
      return this.isTeacher || (this.isManager && this.user.role !== userPrimaryRoles.teacher)
    },
    schoolId() {
      return this.$route.params.schoolId
    },
    isSelected() {
      return !!this.selectedUsers.find(user => user.userId === this.user.userId)
    },
    roles() {
      return rolesOptions
        .filter(option => option.value)
        .map(option => ({ ...option, label: i18n.t(`team.searchbar.roles.${option.label}`) }))
    },
    kreaRoles() {
      return secondaryRolesOptions
        .filter(option => option.value)
        .map(option => ({
          ...option,
          label: i18n.t(`userRoles.secondary.${option.label}`),
          disabled: Boolean(option.value === secondaryRolesIds.approvingAuthor)
        }))
    },
    availableLevels() {
      return this.educationalLevels
        .map(level => ({ ...level, label: i18n.t(`educationalLevels.${level.name.toLowerCase()}`) }))
        .filter(level => this.currentSchool.impartedLevels.includes(level.id))
    },
    showResendInvitation() {
      if (this.isManager && this.user.role !== userPrimaryRoles.teacher) return false
      const hasSended = this.users.find(user => user.userId === this.user.userId).invitationSent
      return this.user.invitationStatus.toLowerCase() !== teamMembersStatus.confirmed && !hasSended
    },
    rowIsTeacher() {
      return this.user.role === userPrimaryRoles.teacher
    },
    rowIsManager() {
      return this.user.role === userPrimaryRoles.manager
    },
    editionRoleIsManager() {
      return this.editionRole === userPrimaryRoles.manager
    },
    editionRoleIsTeacher() {
      return this.editionRole === userPrimaryRoles.teacher
    },
    isConfirmed() {
      return this.user.invitationStatus.toLowerCase() === teamMembersStatus.confirmed
    }
  },
  methods: {
    ...mapActions([
      'selectTeamUser',
      'deselectTeamUser',
      'resendInvitation',
      'showElement',
      'hideElement',
      'editSchoolUser'
    ]),
    translateRole(role) {
      return rolesOptions.find(roleOp => roleOp.label === role.toLowerCase()).value
    },
    translateKreaRole(role) {
      if (!role) return null
      return secondaryRolesOptions.find(roleOp => roleOp.label === toLowerCamelCase(role)).value
    },
    toggleSelect() {
      if (!this.isSelected) this.selectTeamUser(this.user)
      else this.deselectTeamUser(this.user)
    },
    toggleEdition() {
      this.edition = !this.edition
      if (!this.edition) {
        this.editionLevels = this.user.educationalLevels
        this.editionRole = this.user.role
      }
    },
    resend() {
      this.resendInvitation({ schoolId: this.schoolId, id: this.user.userId })
      this.$emit('resendInvitation')
    },
    deleteUser() {
      if (!this.isSelected) this.selectTeamUser(this.user)
      this.showElement({ element: 'deleteMembersModal', props: { single: true } })
    },
    getShortLevel(level) {
      return i18n.t(`educationalLevels.${this.getLevel(level)}`).slice(0, 3) + '.'
    },
    editRole(name, value) {
      this.editionRole = userPrimaryRoles[Object.keys(userPrimaryRoles).find(key => rolesIds[key] === value)]
    },
    editKreaRole(name, value) {
      this.editionKreaRole = userSecondaryRoles[Object.keys(userSecondaryRoles).find(key => secondaryRolesIds[key] === value)]
    },
    editLevels(value, field) {
      this.levelsError = false
      this.editionLevels = value
    },
    async editUser() {
      if (this.editionRoleIsTeacher && !this.editionLevels.length) {
        this.showErrors = true
        this.levelsError = true
        return
      }
      this.showElement({ element: 'fullPageLoader' })
      const success = await this.editSchoolUser({
        schoolId: this.schoolId,
        userData: {
          userId: this.user.userId,
          role: this.editionRole,
          secondaryRole: this.editionKreaRole,
          educationalLevels: this.editionLevels
        }
      })
      this.hideElement({ element: 'fullPageLoader' })
      this.edition = false
      if (success) {
        this.$notify({
          group: 'custom-toast',
          text: i18n.t('team.editionSuccess'),
          type: 'success'
        })
        this.$emit('editedUser', this.pageNumber)
      } else {
        this.$notify({
          group: 'custom-toast',
          text: i18n.t('team.editionError'),
          type: 'error'
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.user{
  min-height: 60px;
  .level{
    border-radius: 50%;
    border: 1px solid $dark-gray;
    width: 32px;
    height: 32px;
  }
  .avatar{
    width: 36px;
    height: 36px;
    border-radius: 50%;
  }
  .email{
    max-width: 300px;
    &.edition{
      max-width: 230px;
    }
  }
  .name{
    max-width: 300px;
    &.edition{
      max-width: 230px;
    }
  }
}

.action{
  border: 1px solid transparent;
  border-radius: 5px;
  &:hover{
    text-decoration: underline;
    text-decoration-color: $gray;
    &.blue{
      text-decoration-color: $blue;
    }
  }
  &.border{
    border: 1px solid $dark-gray;
    padding: 5px 10px;
    text-decoration: none;
    &.blue{
      border-color: $blue;
    }
  }
}

.hidden{
  visibility: hidden;
}

.team-status{
  background-color: $white;
  border: 1px solid $gray;
  display: inline-flex;
  min-width: 100px;
  padding: 5px 10px;
  border-radius: 5px;
  &.green{
    border-color: $green;
  }
  &.red{
    border-color: $red;
  }
  &.dark-gray{
    border-color: $dark-gray;
  }
}

.select{
  max-width: 130px;
}

.custom-tag-input{
  max-width: 200px;
  padding: 0;
}

</style>
